import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import ConsultantDTO from '@src/adapters/dto/consultant/ConsultantDTO';
import { useFindConsultantsLazy } from '@src/adapters/hooks/consultants/useFindConsultants';

const PREFIX = 'ConsultantList';

const classes = {
  root: `${PREFIX}-root`,
  table: `${PREFIX}-table`,
  visuallyHidden: `${PREFIX}-visuallyHidden`,
  header: `${PREFIX}-header`,
  headerCell: `${PREFIX}-headerCell`,
  tableCell: `${PREFIX}-tableCell`,
  roleChip: `${PREFIX}-roleChip`,
};

const Root = styled('div')(({ theme: Theme }) => ({
  [`&.${classes.root}`]: {
    width: '100%',
  },

  [`& .${classes.table}`]: {
    minWidth: 750,
  },

  [`& .${classes.visuallyHidden}`]: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },

  [`& .${classes.header}`]: {
    paddingTop: '10px',
  },

  [`& .${classes.headerCell}`]: {},
  [`& .${classes.tableCell}`]: { height: '60px' },
  [`& .${classes.roleChip}`]: { margin: '10px' },
}));

interface Props {
  selectConsultant(consultant: ConsultantDTO): void;
  selectedConsultant?: ConsultantDTO;
  updatedConsultant?: ConsultantDTO;
}

function List({
  selectedConsultant,
  updatedConsultant,
  selectConsultant,
}: Props) {
  const { execute: find } = useFindConsultantsLazy();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const [consultants, setConsultants] = useState<ConsultantDTO[]>([]);

  const findConsultants = useCallback(() => {
    find({
      pageSize: rowsPerPage,
      currentPage: currentPage,
    }).then((result) => {
      if (result.consultants) {
        setConsultants(
          result.consultants.sort((a, b) =>
            a.lastName.localeCompare(b.lastName)
          )
        );
      }
      if (result.recordCount) {
        setRecordCount(result.recordCount);
      }
    });
  }, [find, rowsPerPage, currentPage]);

  useEffect(() => {
    findConsultants();
  }, [findConsultants]);

  useEffect(() => {
    if (updatedConsultant) {
      setConsultants((prev) =>
        prev.find((c) => c.id === updatedConsultant.id)
          ? prev.map((c) =>
              c.id === updatedConsultant.id ? updatedConsultant : c
            )
          : [...prev, updatedConsultant]
      );
    }
  }, [updatedConsultant]);

  const handleRowClick = (
    event: React.MouseEvent<unknown>,
    consultant: ConsultantDTO
  ) => {
    selectConsultant(consultant);
  };

  const isSelected = (consultant: ConsultantDTO) => {
    return selectedConsultant && selectedConsultant.id === consultant.id;
  };

  return (
    <Root className={classes.root}>
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="Contents table">
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell component="th" align="left">
                Last Name
              </TableCell>
              <TableCell component="th" align="left">
                GMC Reference
              </TableCell>
              <TableCell component="th" align="left">
                Number of Scans
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consultants.map((consultant, index) => {
              const isItemSelected = isSelected(consultant);
              return (
                <TableRow
                  hover
                  onClick={(event) => handleRowClick(event, consultant)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={index}
                  selected={isItemSelected}>
                  <TableCell scope="row">{consultant.firstName}</TableCell>
                  <TableCell scope="row">{consultant.lastName}</TableCell>
                  <TableCell scope="row">{consultant.gmcReference}</TableCell>
                  <TableCell scope="row">
                    {consultant.scans ? consultant.scans.length : 0}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={recordCount}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onPageChange={(event, number) => setCurrentPage(number)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setCurrentPage(0);
        }}
      />
    </Root>
  );
}

export default List;
